import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Payments as PaymentsIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  CalendarMonth as CalendarIcon,
  BarChart as BarChartIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Apps as AppsIcon,
  AdminPanelSettings as AdminIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { colors } from '../theme/expertiseTheme';
import { getAvailableTools, ToolCategory, Tool } from '../services/tools';
import { getStoredUser, isAdmin as checkIsAdmin, isStaff as checkIsStaff } from '../services/auth';

// Mapeamento de ícones
const iconComponents: Record<string, React.ReactNode> = {
  dashboard: <DashboardIcon sx={{ fontSize: 32 }} />,
  person: <PersonIcon sx={{ fontSize: 32 }} />,
  payments: <PaymentsIcon sx={{ fontSize: 32 }} />,
  settings: <SettingsIcon sx={{ fontSize: 32 }} />,
  timeline: <TrendingUpIcon sx={{ fontSize: 32 }} />,
  inventory: <InventoryIcon sx={{ fontSize: 32 }} />,
  people: <PeopleIcon sx={{ fontSize: 32 }} />,
  assessment: <AssessmentIcon sx={{ fontSize: 32 }} />,
  shopping_cart: <ShoppingCartIcon sx={{ fontSize: 32 }} />,
  business: <BusinessIcon sx={{ fontSize: 32 }} />,
  code: <CodeIcon sx={{ fontSize: 32 }} />,
  security: <SecurityIcon sx={{ fontSize: 32 }} />,
  email: <EmailIcon sx={{ fontSize: 32 }} />,
  calendar: <CalendarIcon sx={{ fontSize: 32 }} />,
  bar_chart: <BarChartIcon sx={{ fontSize: 32 }} />,
  attach_money: <AttachMoneyIcon sx={{ fontSize: 32 }} />,
  receipt: <ReceiptIcon sx={{ fontSize: 32 }} />,
  account_balance: <AccountBalanceIcon sx={{ fontSize: 32 }} />,
  admin_panel_settings: <AdminIcon sx={{ fontSize: 32 }} />,
  record_voice_over: <RecordVoiceOverIcon sx={{ fontSize: 32 }} />,
};

const getIcon = (iconName: string): React.ReactNode => {
  return iconComponents[iconName.toLowerCase()] || <AppsIcon sx={{ fontSize: 32 }} />;
};

// Cores por setor
const sectorColors: Record<string, string> = {
  Financeiro: colors.primary,
  Comercial: '#3B82F6',
  TI: '#7C3AED',
  RH: '#EC4899',
  Operacoes: '#F59E0B',
  Comum: '#6B7280',
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<ToolCategory[]>([]);
  const [totalTools, setTotalTools] = useState(0);
  const [totalComingSoon, setTotalComingSoon] = useState(0);

  const user = getStoredUser();
  const isAdmin = checkIsAdmin(user);
  const isStaff = checkIsStaff(user);

  useEffect(() => {
    const loadTools = async () => {
      try {
        const response = await getAvailableTools();
        setCategories(response.categories);
        setTotalTools(response.totalAvailableNow);
        setTotalComingSoon(response.totalComingSoon);
      } catch (err: any) {
        console.error('Erro ao carregar ferramentas:', err);
        setError(err.message || 'Erro ao carregar ferramentas');
        // Fallback com ferramentas padrão baseadas no papel do usuário
        loadFallbackTools();
      } finally {
        setLoading(false);
      }
    };

    loadTools();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFallbackTools = () => {
    const commonTools: Tool[] = [
      {
        id: 'dashboard',
        name: 'Dashboard',
        description: 'Visão geral do sistema',
        icon: 'dashboard',
        route: '/home',
        sector: null,
        comingSoon: false,
      },
      {
        id: 'transcricao',
        name: 'Transcrição de Áudios',
        description: 'Transcreva automaticamente os áudios de uma pasta do Google Drive',
        icon: 'record_voice_over',
        route: '/transcricao',
        sector: null,
        comingSoon: false,
      },
      {
        id: 'profile',
        name: 'Perfil',
        description: 'Gerenciar seu perfil e configurações',
        icon: 'person',
        route: '/configuracoes',
        sector: null,
        comingSoon: false,
      },
    ];

    const financeiroTools: Tool[] = [
      {
        id: 'pagamentos',
        name: 'Pagamentos PIX',
        description: 'Gerenciar e gerar arquivos de pagamento CNAB240/PIX',
        icon: 'payments',
        route: '/financeiro/pagamentos',
        sector: 'Financeiro',
        comingSoon: false,
      },
    ];

    const adminTools: Tool[] = [
      {
        id: 'admin-users',
        name: 'Gerenciar Usuários',
        description: 'Administrar usuários, roles e permissões',
        icon: 'admin_panel_settings',
        route: '/configuracoes',
        sector: null,
        comingSoon: false,
      },
    ];

    const fallbackCategories: ToolCategory[] = [
      { category: 'Comum', tools: commonTools },
    ];

    // Adicionar ferramentas baseadas no papel
    if (isAdmin) {
      fallbackCategories.push({ category: 'Administração', tools: adminTools });
      fallbackCategories.push({ category: 'Financeiro', tools: financeiroTools });
    } else if (isStaff && user?.sector === 'Financeiro') {
      fallbackCategories.push({ category: 'Financeiro', tools: financeiroTools });
    }

    setCategories(fallbackCategories);
    setTotalTools(fallbackCategories.reduce((acc, cat) => acc + cat.tools.length, 0));
  };

  const handleToolClick = (tool: Tool) => {
    if (!tool.comingSoon) {
      navigate(tool.route);
    }
  };

  const getRoleLabel = () => {
    if (isAdmin) return 'Administrador';
    if (isStaff) return 'Staff';
    return 'Usuário';
  };

  const getRoleColor = () => {
    if (isAdmin) return colors.primary;
    if (isStaff) return '#7C3AED';
    return '#6B7280';
  };

  if (loading) {
    return (
      <Layout title="Home">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress sx={{ color: colors.primary }} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout
      title="Home"
      subtitle={`Bem-vindo ao Expertise IA, ${user?.email || 'Usuário'}`}
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* User Info Card */}
      <Card sx={{ mb: 4, border: `1px solid ${colors.border}` }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                backgroundColor: getRoleColor(),
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark }}>
                {user?.email || 'Usuário'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip
                  label={getRoleLabel()}
                  size="small"
                  sx={{
                    backgroundColor: `${getRoleColor()}15`,
                    color: getRoleColor(),
                    fontWeight: 500,
                  }}
                />
                {user?.sector && (
                  <Chip
                    label={user.sector}
                    size="small"
                    sx={{
                      backgroundColor: `${sectorColors[user.sector] || '#6B7280'}15`,
                      color: sectorColors[user.sector] || '#6B7280',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: colors.dark }}>
                {totalTools}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                ferramentas disponíveis
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tools by Category */}
      {categories.map((category) => (
        <Box key={category.category} sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.dark, mb: 0.5 }}>
              {category.category}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {category.tools.length} ferramenta{category.tools.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {category.tools.map((tool) => {
              const toolColor = tool.sector
                ? sectorColors[tool.sector] || colors.primary
                : '#6B7280';
              const isClickable = !tool.comingSoon;

              return (
                <Grid item xs={12} sm={6} md={4} key={tool.id}>
                  <Card
                    onClick={() => handleToolClick(tool)}
                    sx={{
                      height: '100%',
                      cursor: isClickable ? 'pointer' : 'default',
                      opacity: tool.comingSoon ? 0.7 : 1,
                      transition: 'all 0.2s ease',
                      '&:hover': isClickable
                        ? {
                            borderColor: toolColor,
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
                            '& .arrow-icon': {
                              transform: 'translateX(4px)',
                              opacity: 1,
                            },
                          }
                        : {
                            transform: 'none',
                            borderColor: colors.border,
                            boxShadow: 'none',
                          },
                    }}
                  >
                    <CardContent
                      sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            backgroundColor: `${toolColor}15`,
                            color: toolColor,
                          }}
                        >
                          {getIcon(tool.icon)}
                        </Avatar>
                        <Chip
                          label={tool.comingSoon ? 'Em breve' : 'Ativo'}
                          size="small"
                          sx={{
                            backgroundColor: tool.comingSoon
                              ? colors.light
                              : 'rgba(0, 99, 0, 0.1)',
                            color: tool.comingSoon ? colors.text.secondary : colors.primary,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: colors.dark,
                          mb: 1,
                        }}
                      >
                        {tool.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          lineHeight: 1.6,
                          flex: 1,
                          mb: 2,
                        }}
                      >
                        {tool.description}
                      </Typography>

                      {tool.comingSoon ? (
                        <Box sx={{ pt: 2, borderTop: `1px solid ${colors.border}` }}>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: colors.text.light,
                              mb: 1,
                            }}
                          >
                            Em desenvolvimento
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={35}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: colors.border,
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: toolColor,
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            pt: 2,
                            borderTop: `1px solid ${colors.border}`,
                          }}
                        >
                          <ArrowForwardIcon
                            className="arrow-icon"
                            sx={{
                              color: toolColor,
                              opacity: 0.5,
                              transition: 'all 0.2s ease',
                            }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {/* Info about coming soon tools */}
      {totalComingSoon > 0 && (
        <Alert
          severity="info"
          sx={{
            mt: 2,
            borderRadius: '12px',
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <Typography variant="body2">
            <strong>{totalComingSoon}</strong> ferramenta{totalComingSoon !== 1 ? 's' : ''} em
            desenvolvimento. Fique ligado para novidades!
          </Typography>
        </Alert>
      )}
    </Layout>
  );
};

export default HomePage;
